/* eslint-disable max-lines, max-lines-per-function, id-length */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { TableUtility } from '@utility/dynamic-table';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';

import dayjs from 'dayjs';
import { useSnackbar } from '../../../../Components/Snackbar/snackBarProvider';
import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../../../../Api/resources/serviceURLs';
import { serviceName } from '../../../../Api/resources/serviceConstants';
import { useAuth } from '../../../../Contexts/AuthContext';
import { MODULE_NAMES } from '../../../../Constants/modules';

import editIcon from '../../../../Assets/images/svg/dar_edit.svg';
import deleteIcon from '../../../../Assets/images/svg/dar_delete.svg';
import './ResourceLoadingApprovalHistory.scss';

const RESOURCE_LOADING_BASE_PATH = '/v1/resource-loading-plan';
const PAGE_SIZE = 10;
const DEFAULT_APPROVAL_HISTORY_FILTERS = [{
  field: 'status',
  template: 'multiSelect',
  selectedValues: ['Open'],
}];
const APPROVAL_HISTORY_FILTER_FIELDS = new Set([
  'projectName',
  'vertical',
  'projectType',
  'status',
]);

const getStoredAuthToken = () => localStorage.getItem('authToken') || '';

const buildProjectServiceApiUrl = (path) => {
  const serviceURL = appendServiceNameToURL(serviceName.PROJECT_MS);
  const servicePort = appendServicePortToURL(serviceName.PROJECT_MS);
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api${path}`;
  }

  return `${baseUrl}:${servicePort}${serviceURL}/api${path}`;
};

const normalizeErrorMessage = (responseData, fallbackMessage) => (
  responseData?.message
  || responseData?.error
  || fallbackMessage
);

const formatDisplayDate = (value) => {
  if (!value) return '-';
  const parsedDate = dayjs(value);
  return parsedDate.isValid() ? parsedDate.format('DD-MMM-YYYY') : '-';
};

const toFilterOption = (value) => {
  const normalized = String(value ?? '').trim();
  if (!normalized || normalized === '-') return null;

  return {
    id: normalized,
    name: normalized,
    label: normalized,
    value: normalized,
  };
};

const buildFilterOptions = (values = []) => (
  Array.from(
    new Map(
      values
        .map(toFilterOption)
        .filter(Boolean)
        .map((option) => [option.value, option]),
    ).values(),
  )
);

const normalizeFilterComparableValue = (value) => {
  if (value === null || value === undefined) return '';

  if (typeof value === 'object') {
    return normalizeFilterComparableValue(
      value.value ?? value.id ?? value.name ?? value.label,
    );
  }

  return String(value)
    .trim()
    .toLowerCase();
};

const getFilterDisplayValue = (value) => {
  if (value === null || value === undefined) return '';

  if (typeof value === 'object') {
    return getFilterDisplayValue(
      value.value ?? value.name ?? value.label ?? value.id,
    );
  }

  return String(value).trim();
};

const getUniqueFilterValues = (values = [], valueMapper = (value) => value) => (
  Array.from(
    new Map(
      values
        .map(valueMapper)
        .filter(Boolean)
        .map((value) => [normalizeFilterComparableValue(value), value]),
    ).values(),
  )
);

const getSelectedApprovalHistoryFilterValues = (filterItem = {}) => {
  const selectedValues = Array.isArray(filterItem?.selectedValues)
    ? filterItem.selectedValues
    : [];
  const fallbackValues = [
    filterItem?.selectedValue,
    filterItem?.value,
  ];

  return getUniqueFilterValues(
    [...selectedValues, ...fallbackValues],
    normalizeFilterComparableValue,
  );
};

const getApprovalHistoryFilterDisplayValues = (filters = [], field = '') => {
  const matchingValues = (Array.isArray(filters) ? filters : [])
    .filter((filterItem) => filterItem?.field === field)
    .flatMap((filterItem) => {
      const selectedValues = Array.isArray(filterItem?.selectedValues)
        ? filterItem.selectedValues
        : [];

      return [
        ...selectedValues,
        filterItem?.selectedValue,
        filterItem?.value,
      ];
    });

  return getUniqueFilterValues(matchingValues, getFilterDisplayValue);
};

const getApprovalHistoryDefaultFilterValues = (
  filters = [],
  field = '',
  fallbackValues = [],
) => {
  const appliedValues = getApprovalHistoryFilterDisplayValues(filters, field);

  return appliedValues.length > 0 ? appliedValues : fallbackValues;
};

const getApprovalHistoryFilterKey = (filters = []) => (
  JSON.stringify(
    (Array.isArray(filters) ? filters : [])
      .filter((filterItem) => APPROVAL_HISTORY_FILTER_FIELDS.has(filterItem?.field))
      .map((filterItem) => ({
        field: filterItem.field,
        values: getSelectedApprovalHistoryFilterValues(filterItem),
      }))
      .filter((filterItem) => filterItem.values.length > 0),
  )
);

const applyApprovalHistoryFilters = (rows = [], filters = []) => {
  const activeFilterGroups = (Array.isArray(filters) ? filters : [])
    .reduce((filterGroups, filterItem) => {
      if (!APPROVAL_HISTORY_FILTER_FIELDS.has(filterItem?.field)) {
        return filterGroups;
      }

      const selectedValues = getSelectedApprovalHistoryFilterValues(filterItem);

      if (selectedValues.length === 0) {
        return filterGroups;
      }

      const existingValues = filterGroups.get(filterItem.field) || new Set();
      selectedValues.forEach((selectedValue) => existingValues.add(selectedValue));
      filterGroups.set(filterItem.field, existingValues);

      return filterGroups;
    }, new Map());

  if (activeFilterGroups.size === 0) {
    return rows;
  }

  return rows.filter((row) => (
    Array.from(activeFilterGroups.entries()).every(([field, selectedValues]) => {
      const rowValue = normalizeFilterComparableValue(row?.[field]);

      return selectedValues.has(rowValue);
    })
  ));
};

const buildFilterMenuItems = ({
  field,
  header,
  template,
  options = [],
  defaultSelectedValues = [],
}) => {
  const isMultiValueTemplate = ['multiSelect', 'choice'].includes(template);
  const mappedDefaults = getUniqueFilterValues(defaultSelectedValues, getFilterDisplayValue);
  const selectedValues = isMultiValueTemplate
    ? mappedDefaults
    : mappedDefaults.map(toFilterOption);
  const defaultObj = mappedDefaults[0] || null;

  return [{
    label: header,
    items: [{
      type: 'filter',
      field,
      label: header,
      placeholder: `Select ${header}`,
      template,
      selectedValues: selectedValues.length > 0 ? selectedValues : (template === 'dateRange' ? [null, null] : []),
      selectedValue: isMultiValueTemplate ? null : toFilterOption(defaultObj),
      value: isMultiValueTemplate ? null : defaultObj || null,
      ...(template === 'inputfield' ? { inputValue: '' } : {}),
      ...(template === 'dateRange' ? { range: [null, null] } : {}),
      options,
      values: options,
    }],
  }];
};


const buildApprovalHistoryActionCell = (row = {}, onEdit, onDelete, canEdit = false, canDelete = false) => {
  if (!canEdit && !canDelete) {
    return '-';
  }

  const isOpenStatus = String(row?.status || '')
    .trim()
    .toLowerCase() === 'open';
  const editDisabled = !canEdit || !isOpenStatus;
  const deleteDisabled = !canDelete || !isOpenStatus;

  return (
    <div
      className="resource-loading-approval-history-action-cell"
      role="presentation"
      onClick={(event) => event.stopPropagation()}
    >
      {canEdit ? (
        <button
          type="button"
          className={`resource-loading-approval-history-action-btn ${editDisabled ? 'is-disabled' : ''}`}
          aria-label={`Edit approval history row ${row?.requestNumber || ''}`}
          title="Edit"
          disabled={editDisabled}
          onClick={(event) => {
            event.stopPropagation();
            if (!editDisabled && onEdit) onEdit(row);
          }}
        >
          <img src={editIcon} alt="" aria-hidden="true" />
        </button>
      ) : null}
      {canDelete ? (
        <button
          type="button"
          className={`resource-loading-approval-history-action-btn ${deleteDisabled ? 'is-disabled' : ''}`}
          aria-label={`Delete approval history row ${row?.requestNumber || ''}`}
          title="Delete"
          disabled={deleteDisabled}
          onClick={(event) => {
            event.stopPropagation();
            if (!deleteDisabled && onDelete) onDelete(row);
          }}
        >
          <img src={deleteIcon} alt="" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
};


const buildApprovalHistoryColumns = (
  rows = [],
  masters = {},
  appliedFilters = DEFAULT_APPROVAL_HISTORY_FILTERS,
) => {
  const projectOptions = buildFilterOptions(masters?.projects || rows.map((row) => row?.projectName));
  const verticalOptions = buildFilterOptions(masters?.verticals || rows.map((row) => row?.vertical));
  const typeOptions = buildFilterOptions(masters?.types || rows.map((row) => row?.projectType));
  const statusOptions = buildFilterOptions([
    'Open',
    'Approved',
    'Rejected',
  ]);

  return [
    {
      field: 'requestNumber',
      header: 'RN#',
      visible: true,
      sortable: false,
      filter: false,
      pinnable: false,
      defaultPinned: false,
      frozen: false,
      width: 120,
      isGlobalFilter: true,
      showFilterButtonInHeader: false,
      showPinButtonInHeader: false,
      menuItems: [],
    },
    {
      field: 'requesterName',
      header: 'Req By',
      visible: true,
      sortable: false,
      filter: false,
      pinnable: false,
      width: 170,
      isGlobalFilter: true,
      showFilterButtonInHeader: false,
      showPinButtonInHeader: false,
      menuItems: [],
    },
    {
      field: 'requestedDateDisplay',
      header: 'Date',
      visible: true,
      sortable: false,
      filter: false,
      pinnable: false,
      width: 150,
      isGlobalFilter: true,
      showFilterButtonInHeader: false,
      showPinButtonInHeader: false,
      menuItems: [],
    },
    {
      field: 'projectName',
      header: 'Project Name',
      visible: true,
      sortable: false,
      filter: false,
      pinnable: false,
      width: 240,
      isGlobalFilter: true,
      showFilterButtonInHeader: false,
      showPinButtonInHeader: false,
      menuItems: buildFilterMenuItems({
        field: 'projectName',
        header: 'Project Name',
        template: 'multiSelect',
        options: projectOptions,
        defaultSelectedValues: getApprovalHistoryDefaultFilterValues(
          appliedFilters,
          'projectName',
        ),
      }),
    },
    {
      field: 'vertical',
      header: 'Project Vertical',
      visible: true,
      sortable: false,
      filter: false,
      pinnable: false,
      width: 170,
      isGlobalFilter: true,
      showFilterButtonInHeader: false,
      showPinButtonInHeader: false,
      menuItems: buildFilterMenuItems({
        field: 'vertical',
        header: 'Project Vertical',
        template: 'multiSelect',
        options: verticalOptions,
        defaultSelectedValues: getApprovalHistoryDefaultFilterValues(appliedFilters, 'vertical'),
      }),
    },
    {
      field: 'projectType',
      header: 'Project Type',
      visible: false,
      sortable: false,
      filter: false,
      pinnable: false,
      isGlobalFilter: true,
      showFilterButtonInHeader: false,
      showPinButtonInHeader: false,
      menuItems: buildFilterMenuItems({
        field: 'projectType',
        header: 'Project Type',
        template: 'multiSelect',
        options: typeOptions,
        defaultSelectedValues: getApprovalHistoryDefaultFilterValues(appliedFilters, 'projectType'),
      }),
    },
    {
      field: 'approverName',
      header: 'Approvers',
      visible: true,
      sortable: false,
      filter: false,
      pinnable: false,
      width: 190,
      isGlobalFilter: true,
      showFilterButtonInHeader: false,
      showPinButtonInHeader: false,
      menuItems: [],
    },
    {
      field: 'status',
      header: 'Project Status',
      visible: true,
      sortable: false,
      filter: false,
      pinnable: false,
      width: 170,
      isGlobalFilter: true,
      showFilterButtonInHeader: false,
      showPinButtonInHeader: false,
      menuItems: buildFilterMenuItems({
        field: 'status',
        header: 'Project Status',
        template: 'multiSelect',
        options: statusOptions,
        defaultSelectedValues: getApprovalHistoryDefaultFilterValues(appliedFilters, 'status'),
      }),
    },
    {
      field: 'action',
      header: 'Action',
      visible: true,
      sortable: false,
      filter: false,
      pinnable: false,
      width: 82,
      minWidth: 82,
      maxWidth: 82,
      exportable: false,
      className: 'resource-loading-approval-history-action-column',
      headerClassName: 'resource-loading-approval-history-action-column',
      bodyClassName: 'resource-loading-approval-history-action-column',
      menuItems: [],
    },
  ];
};

const buildApprovalHistoryToolbarHeading = (handleBack) => (
  <div className="resource-loading-approval-history-toolbar-title">
    <button
      type="button"
      className="resource-loading-approval-history-toolbar-title__back"
      onClick={handleBack}
      aria-label="Back to resource loading plan"
    >
      <ArrowBackIosNewRoundedIcon fontSize="inherit" />
    </button>
    <span className="resource-loading-approval-history-toolbar-title__text">
      Resource Loading Approval History
    </span>
  </div>
);

const buildApprovalHistoryConfiguration = ({
  handleBack,
  handleFilterUpdate,
  handleStaffingRequestClick,
  tableConfig,
}) => ({
  generalSettings: {
    enableApiFiltering: false,
    showToolBar: true,
    showPagination: true,
    baseURL: '',
    headers: {
      token: getStoredAuthToken(),
    },
    apiEndpoints: {
      getAllViews: { endpoint: '', enabled: false },
      getFilteredData: { endpoint: '', enabled: false },
      saveOrUpdateView: { endpoint: '', enabled: false },
      deleteView: { endpoint: '', enabled: false },
      download: {
        enabled: true,
        filename: () => 'Resource_Loading_Approval_History',
        sheetName: () => 'Approval History',
        transformResponse: (rows) => ({
          tableConfiguration: [
            { field: 'requestNumber', header: 'RN#', width: 120 },
            { field: 'requesterName', header: 'Req By', width: 170 },
            { field: 'requestedDateDisplay', header: 'Date', width: 150 },
            { field: 'projectName', header: 'Project Name', width: 240 },
            { field: 'vertical', header: 'Project Vertical', width: 170 },
            { field: 'approverName', header: 'Approvers', width: 190 },
            { field: 'status', header: 'Project Status', width: 170 },
          ],
          tableData: (rows || []).map((row) => ({
            requestNumber: row?.requestNumber || '-',
            requesterName: row?.requesterName || '-',
            requestedDateDisplay: row?.requestedDateDisplay || '-',
            projectName: row?.projectName || '-',
            vertical: row?.vertical || '-',
            approverName: row?.approverName || '-',
            status: row?.status || '-',
          })),
        }),
      },
    },
    handler: {
      onFilterUpdate: handleFilterUpdate,
    },
    toolBarSettings: {
      showAddNew: false,
      showDownloadExcel: true,
      showDownloadCsv: false,
      showColumnCustomize: false,
      showColumnFilterMenu: true,
      showHelp: false,
      showSearch: false,
      showSaveView: false,
      showReset: false,
      showExpandCollapse: false,
      showFilterOption: true,
      showToolBarHeading: true,
      toolBarHeading: buildApprovalHistoryToolbarHeading(handleBack),
      additionalElements: [
        <button
          key="staffing-request-link"
          type="button"
          className="resource-loading-approval-history-toolbar-link"
          onClick={handleStaffingRequestClick}
        >
          Staffing Request
        </button>,
      ],
    },
  },
  tableSettings: {
    size: 'small',
    pageSize: PAGE_SIZE,
    tableConfig,
    tableScrollHeight: 'auto',
    loaderSkeleton: {
      rows: PAGE_SIZE,
      bodyCells: 1,
      columns: Math.max(tableConfig.length, 1),
    },
    paginationConfig: {
      page: 1,
      limit: PAGE_SIZE,
      total: 0,
      rowsPerPageOptions: [10, 15, 25, 50],
    },
    showExpandCollapse: false,
    maxFrozenColumns: {
      desktop: 1,
      tablet: 1,
      mobile: 1,
    },
  },
});

const getPermissionActions = (permissions = {}, moduleName = '') => {
  const moduleCandidates = [
    moduleName,
    moduleName.replace(/-/g, '_'),
    moduleName.replace(/_/g, '-'),
  ];

  for (const candidate of moduleCandidates) {
    if (Array.isArray(permissions?.[candidate])) {
      return permissions[candidate];
    }
  }

  return [];
};

const hasPermissionAction = (permissions = {}, moduleName = '', action = '') => (
  getPermissionActions(permissions, moduleName).includes(action)
);

const ApprovalHistoryTableSection = React.memo(({
  configuration,
  data,
  loading,
  tableKey,
}) => (
  <div className="resource-loading-approval-history-table-utility">
    <TableUtility
      key={tableKey}
      configuration={configuration}
      data={data}
      loading={loading}
      showFilterOption
      useCustomBodyRenderer
    />
  </div>
));

ApprovalHistoryTableSection.displayName = 'ApprovalHistoryTableSection';

ApprovalHistoryTableSection.propTypes = {
  configuration: PropTypes.shape({}).isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  loading: PropTypes.bool.isRequired,
  tableKey: PropTypes.string.isRequired,
};

const ResourceLoadingApprovalHistory = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { snackbarOpen } = useSnackbar();
  const { userProfile } = useAuth();
  const [approvalHistoryRows, setApprovalHistoryRows] = useState([]);
  const [approvalHistoryFilters, setApprovalHistoryFilters] = useState(
    DEFAULT_APPROVAL_HISTORY_FILTERS,
  );
  const [approvalHistoryMasters, setApprovalHistoryMasters] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [editModalState, setEditModalState] = useState({
    isOpen: false,
    row: null,
    action: 'approve',
    remarks: '',
  });
  const [deleteModalState, setDeleteModalState] = useState({
    isOpen: false,
    row: null,
  });

  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const projectId = queryParams.get('projectId');
  const projectName = location.state?.projectName || queryParams.get('projectName') || 'Project';
  const projectVertical = location.state?.projectVertical || queryParams.get('projectVertical') || '-';
  const returnSearch = location.state?.returnSearch
    || (projectId ? `?projectId=${projectId}` : '');

  const userPermissions = userProfile?.permissions || {};
  const isSuperAdmin = Boolean(userProfile?.superAdmin || userProfile?.super_admin);
  const historyModule = MODULE_NAMES.DELIVERY_RESOURCE_LOADING_APPROVAL_HISTORY;
  const canApproveReject = isSuperAdmin
    || hasPermissionAction(userPermissions, historyModule, 'update');
  const canDeleteRequest = isSuperAdmin
    || hasPermissionAction(userPermissions, historyModule, 'delete');

  const approvalHistoryUserInfo = useMemo(() => {
    const planModuleName = MODULE_NAMES.DELIVERY_RESOURCE_LOADING_PLAN;
    const historyModuleName = MODULE_NAMES.DELIVERY_RESOURCE_LOADING_APPROVAL_HISTORY;

    return {
      emailId: userProfile?.email,
      employeeId: userProfile?.employeeId,
      fullName: userProfile?.user_name,
      permissions: {
        [planModuleName]: userProfile?.permissions?.[planModuleName] || [],
        [historyModuleName]: userProfile?.permissions?.[historyModuleName] || [],
      },
      roleDetails: userProfile?.roleDetails || [],
      superAdmin: isSuperAdmin,
      globalColors: userProfile?.siteConfiguration?.global_colors,
    };
  }, [
    userProfile?.email,
    userProfile?.employeeId,
    userProfile?.permissions,
    userProfile?.roleDetails,
    userProfile?.siteConfiguration?.global_colors,
    isSuperAdmin,
    userProfile?.user_name,
  ]);

  const hasResolvedUserPermissions = Boolean(userProfile?.permissions);

  const handleBack = useCallback(() => {
    navigate(`/delivery/project-details/resource-loading-plan${returnSearch}`);
  }, [navigate, returnSearch]);

  const handleStaffingRequestClick = useCallback(() => {
    window.open('/delivery/staffing-request', '_blank');
  }, []);

  const loadApprovalHistory = useCallback(async () => {
    if (!projectId) {
      snackbarOpen('Project id is required to open approval history.', 'error');
      setIsLoading(false);
      return;
    }

    if (!hasResolvedUserPermissions) {
      return;
    }

    const hasReadAccess = isSuperAdmin || hasPermissionAction(
      approvalHistoryUserInfo.permissions,
      MODULE_NAMES.DELIVERY_RESOURCE_LOADING_APPROVAL_HISTORY,
      'read',
    );

    if (!hasReadAccess) {
      snackbarOpen('You do not have permission to view approval history.', 'error');
      setIsLoading(false);
      handleBack();
      return;
    }

    setIsLoading(true);

    try {
      const requestUrl = buildProjectServiceApiUrl(
        `${RESOURCE_LOADING_BASE_PATH}/approval-history`,
      );

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          token: getStoredAuthToken(),
        },
        body: JSON.stringify({
          projectId,
          userInfo: approvalHistoryUserInfo,
        }),
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          normalizeErrorMessage(responseData, 'Failed to load approval history.'),
        );
      }

      const rawRows = responseData?.data?.approvalHistory
        || (Array.isArray(responseData?.data) ? responseData.data : []);
      const nextRows = rawRows.map((row, index) => ({
        id: row?.requestNumber || row?.res_load_id || row?.id || index + 1,
        rawRequestNumber: row?.requestNumber,
        requestNumber: row?.requestNumber ? `RN-${row.requestNumber}` : '-',
        requesterName: row?.requesterName || '-',
        requestedDate: row?.requestedDate || '',
        requestedDateDisplay: formatDisplayDate(row?.requestedDate),
        projectName: row?.projectName || projectName || '-',
        vertical: row?.vertical || row?.projectVertical || projectVertical || '-',
        projectType: row?.projectType || '-',
        approverName: row?.approverName || '-',
        status: row?.status || '-',
      }));

      const targetProjectName = responseData?.data?.projectName || projectName;
      setApprovalHistoryRows(nextRows);
      setApprovalHistoryMasters(responseData?.data?.masters || {});

      const isAllMode = queryParams.get('mode') === 'all';
      if (projectId && !isAllMode && targetProjectName && targetProjectName !== 'Project') {
        setApprovalHistoryFilters((prevFilters) => {
          const hasProjectFilter = prevFilters.some(
            (filterItem) => filterItem?.field === 'projectName',
          );
          if (!hasProjectFilter) {
            return [
              ...prevFilters,
              {
                field: 'projectName',
                template: 'multiSelect',
                selectedValues: [targetProjectName],
              },
            ];
          }
          return prevFilters;
        });
      }
    } catch (error) {
      snackbarOpen(
        error?.message || 'Failed to load approval history.',
        'error',
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    approvalHistoryUserInfo,
    hasResolvedUserPermissions,
    projectName,
    projectVertical,
    projectId,
    queryParams,
    snackbarOpen,
  ]);

  useEffect(() => {
    loadApprovalHistory();
  }, [loadApprovalHistory]);

  useEffect(() => {
    const syncHighlight = () => {
      const filterBtn = document.querySelector(
        '.resource-loading-approval-history-table-utility .table-utility-filter-btn',
      );
      if (!filterBtn) return;
      const hasActive = Boolean(
        document.querySelector('.p-column-filter-menu-button-active, .p-column-filter-active'),
      );
      filterBtn.classList.toggle('active', hasActive);
    };

    const container = document.querySelector('.resource-loading-approval-history-table-utility');
    if (container) {
      container.addEventListener('click', () => setTimeout(syncHighlight, 150));
    }
    const interval = setInterval(syncHighlight, 1000);
    setTimeout(syncHighlight, 500);

    return () => {
      clearInterval(interval);
      if (container) {
        container.removeEventListener('click', syncHighlight);
      }
    };
  }, []);

  const handleOpenEdit = useCallback((row) => {
    setEditModalState({
      isOpen: true,
      row,
      action: row?.status?.toLowerCase() === 'approved' ? 'approve' : 'reject',
      remarks: '',
    });
  }, []);

  const handleSaveEdit = useCallback(async () => {
    const rawReqNum = editModalState.row?.rawRequestNumber || editModalState.row?.id;
    if (!rawReqNum) return;

    try {
      const requestUrl = buildProjectServiceApiUrl(`${RESOURCE_LOADING_BASE_PATH}/approval/${rawReqNum}`);
      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          token: getStoredAuthToken(),
        },
        body: JSON.stringify({
          action: editModalState.action,
          remarks: editModalState.remarks,
          userInfo: approvalHistoryUserInfo,
        }),
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(normalizeErrorMessage(responseData, 'Failed to update approval status.'));
      }

      snackbarOpen('Approval status updated successfully.', 'success');
      setEditModalState({ isOpen: false, row: null, action: 'approve', remarks: '' });
      loadApprovalHistory();
    } catch (error) {
      snackbarOpen(error?.message || 'Failed to update approval status.', 'error');
    }
  }, [approvalHistoryUserInfo, editModalState, loadApprovalHistory, snackbarOpen]);

  const handleOpenDelete = useCallback((row) => {
    setDeleteModalState({
      isOpen: true,
      row,
    });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    const rawReqNum = deleteModalState.row?.rawRequestNumber || deleteModalState.row?.id;
    if (!rawReqNum) return;

    try {
      const requestUrl = buildProjectServiceApiUrl(`${RESOURCE_LOADING_BASE_PATH}/approval/${rawReqNum}`);
      const response = await fetch(requestUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          token: getStoredAuthToken(),
        },
        body: JSON.stringify({
          userInfo: approvalHistoryUserInfo,
        }),
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(normalizeErrorMessage(responseData, 'Failed to delete approval request.'));
      }

      snackbarOpen('Approval request deleted successfully.', 'success');
      setDeleteModalState({ isOpen: false, row: null });
      loadApprovalHistory();
    } catch (error) {
      snackbarOpen(error?.message || 'Failed to delete approval request.', 'error');
    }
  }, [approvalHistoryUserInfo, deleteModalState.row, loadApprovalHistory, snackbarOpen]);

  const handleApprovalHistoryFilterUpdate = useCallback(({ filters } = {}) => {
    setApprovalHistoryFilters(Array.isArray(filters) ? filters : []);
  }, []);

  const filteredApprovalHistoryRows = useMemo(
    () => applyApprovalHistoryFilters(approvalHistoryRows, approvalHistoryFilters),
    [approvalHistoryFilters, approvalHistoryRows],
  );

  const approvalHistoryTableKey = useMemo(
    () => `approval-history-${getApprovalHistoryFilterKey(approvalHistoryFilters)}`,
    [approvalHistoryFilters],
  );

  const approvalHistoryRowsWithActions = useMemo(
    () => filteredApprovalHistoryRows.map((row) => ({
      ...row,
      action: buildApprovalHistoryActionCell(
        row,
        handleOpenEdit,
        handleOpenDelete,
        canApproveReject,
        canDeleteRequest,
      ),
    })),
    [filteredApprovalHistoryRows, handleOpenDelete, handleOpenEdit, canApproveReject, canDeleteRequest],
  );

  const tableConfiguration = useMemo(
    () => buildApprovalHistoryConfiguration({
      handleBack,
      handleFilterUpdate: handleApprovalHistoryFilterUpdate,
      handleStaffingRequestClick,
      tableConfig: buildApprovalHistoryColumns(
        approvalHistoryRows,
        approvalHistoryMasters,
        approvalHistoryFilters,
      ),
    }),
    [
      approvalHistoryFilters,
      approvalHistoryMasters,
      approvalHistoryRows,
      handleBack,
      handleApprovalHistoryFilterUpdate,
      handleStaffingRequestClick,
    ],
  );

  const isEditSaveDisabled = !editModalState.action || editModalState.remarks.trim().length === 0;

  return (
    <section className="resource-loading-approval-history-page">
      <div className="resource-loading-approval-history-shell">
        <ApprovalHistoryTableSection
          configuration={tableConfiguration}
          data={approvalHistoryRowsWithActions}
          loading={isLoading}
          tableKey={approvalHistoryTableKey}
        />
      </div>

      {editModalState.isOpen ? (
        <div
          className="resource-loading-approval-history-comment-backdrop"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setEditModalState((prev) => ({ ...prev, isOpen: false }));
            }
          }}
        >
          <div className="resource-loading-approval-history-comment-modal">
            <div className="resource-loading-approval-history-comment-modal-header">Update Status</div>
            <div className="resource-loading-approval-history-comment-modal-body">
              <label
                className="resource-loading-approval-history-comment-modal-label"
                htmlFor="resource-loading-approval-history-status"
              >
                Status*
              </label>
              <div className="resource-loading-approval-history-comment-modal-select-wrap">
                <select
                  id="resource-loading-approval-history-status"
                  className="resource-loading-approval-history-comment-modal-select"
                  value={editModalState.action}
                  onChange={(event) => setEditModalState((prev) => ({ ...prev, action: event.target.value }))}
                >
                  <option value="" disabled>Select status</option>
                  <option value="approve">Approve</option>
                  <option value="reject">Reject</option>
                </select>
              </div>
              <label
                className="resource-loading-approval-history-comment-modal-label"
                htmlFor="resource-loading-approval-history-comment"
              >
                Comments*
              </label>
              <textarea
                id="resource-loading-approval-history-comment"
                className="resource-loading-approval-history-comment-modal-input"
                value={editModalState.remarks}
                onChange={(event) => setEditModalState((prev) => ({ ...prev, remarks: event.target.value }))}
                placeholder="Enter..."
                rows={4}
              />
            </div>
            <div className="resource-loading-approval-history-comment-modal-footer">
              <button
                type="button"
                className={[
                  'resource-loading-approval-history-comment-modal-btn',
                  'resource-loading-approval-history-comment-modal-btn-ghost',
                ].join(' ')}
                onClick={() => setEditModalState((prev) => ({ ...prev, isOpen: false }))}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                className={[
                  'resource-loading-approval-history-comment-modal-btn',
                  'resource-loading-approval-history-comment-modal-btn-primary',
                ].join(' ')}
                disabled={isEditSaveDisabled}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <Dialog
        open={deleteModalState.isOpen}
        onClose={() => setDeleteModalState((prev) => ({ ...prev, isOpen: false }))}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete Approval Request</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this approval request?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModalState((prev) => ({ ...prev, isOpen: false }))}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  );
};

export default ResourceLoadingApprovalHistory;
